import axios, { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { getOptionalAuthHeader, UNAUTHORIZED_MESSAGES } from '../../auth/utils';
import { AuthenticationError, HttpError } from '../errors/errors';

type UploadProgress = AxiosProgressEvent;

type RequestConfig = {
  headers?: { [key: string]: string };
  onUploadProgress?: (progress: UploadProgress) => void;
  omitAuthHeader?: boolean;
};

const axiosInstance = axios.create({ baseURL: '/' });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    return new Promise((resolve, reject) => {
      if (error instanceof axios.Cancel) {
        return;
      }

      // Intercept network errors
      if (error.code === 'ERR_NETWORK') {
        reject(new HttpError(error.message));
        return;
      }

      // Intercept XML response errors (e.g. S3 bucket upload)
      if (typeof error.response.data === 'string') {
        const isXml = error.response.data?.startsWith('<?xml');
        const xmlError = error.response.data?.match(/<Error>.*<Message>(.*)<\/Message>.*<\/Error>/)?.[1];
        if (isXml && !!xmlError) {
          reject(new HttpError(xmlError));
          return;
        }
      }

      const errorMessages = error?.response?.data?.errors || [];
      const statusCode = error?.response?.status;

      // In case of invalid or missing API Key, the server returns a 400 error with a specific error message:
      // this means that we either don't have a valid session set or that the API key has expired or changed.
      // Force redirection to log out to push user out of the app
      const matchedError = errorMessages.find((m: string) => UNAUTHORIZED_MESSAGES.includes(m));
      const isBadApiKey = statusCode === 400 && !!matchedError;
      if (isBadApiKey) {
        reject(new AuthenticationError());
        return;
      }

      // For any other error, return the server message (including invalid login credentials)
      reject(new HttpError(errorMessages[0]));
    });
  }
);

export class BaseRESTService {
  private cancelTokenSource = axios.CancelToken.source();

  cancelAllRequests() {
    this.cancelTokenSource.cancel();
    // Recreate new token for next usage
    this.cancelTokenSource = axios.CancelToken.source();
  }

  get(url: string, config: RequestConfig = {}) {
    return axiosInstance.get(url, this.getFullConfig(config));
  }

  delete(url: string, config: RequestConfig = {}) {
    return axiosInstance.delete(url, this.getFullConfig(config));
  }

  head(url: string, config: RequestConfig = {}) {
    return axiosInstance.head(url, this.getFullConfig(config));
  }

  options(url: string, config: RequestConfig = {}) {
    return axiosInstance.options(url, this.getFullConfig(config));
  }

  post(url: string, data: any, config: RequestConfig = {}) {
    return axiosInstance.post(url, data, this.getFullConfig(config));
  }

  put(url: string, data: any, config: RequestConfig = {}) {
    return axiosInstance.put(url, data, this.getFullConfig(config));
  }

  private getFullConfig(config: RequestConfig = {}): AxiosRequestConfig {
    const authHeader = config.omitAuthHeader ? null : getOptionalAuthHeader();
    return {
      ...config,
      headers: { ...(config.headers ?? {}), ...(authHeader ?? {}) },
      cancelToken: this.cancelTokenSource.token,
    };
  }
}
